import { TrendingUp, Wallet, Calculator, Download, Calendar as CalendarIcon, Trash2, Presentation } from 'lucide-react';
import { useState, useCallback, useMemo } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { ru } from 'date-fns/locale';
import pptxgen from 'pptxgenjs';

interface DashboardViewProps {
  allTransactions: any[];
  currentUser: any;
  allUsers: any[];
  onOpenProfit: () => void;
  onTransactionDeleted?: () => void;
  isDark: boolean;
}

type DateFilter = 'month' | 'year' | 'all' | 'custom';

export default function DashboardView({ allTransactions, currentUser, allUsers, onOpenProfit, onTransactionDeleted, isDark }: DashboardViewProps) {
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [customDateRange, setCustomDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const formatNumber = (num: number) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    return Number(num).toLocaleString('ru-RU');
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    if (!confirm('Удалить эту транзакцию?')) return;
    
    setDeletingId(transactionId);
    try {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', transactionId);

      if (error) throw error;
      
      toast.success('Транзакция удалена');
      
      // Вызываем callback для обновления данных в родительском компоненте
      if (onTransactionDeleted) {
        onTransactionDeleted();
      }
    } catch (error: any) {
      console.error('Delete transaction error:', error);
      toast.error('Ошибка удаления: ' + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  const getFilteredTransactions = () => {
    let filtered = allTransactions.filter(t => t.user_id === currentUser?.id);
    const now = new Date();

    switch (dateFilter) {
      case 'month':
        const monthStart = startOfMonth(now);
        const monthEnd = endOfMonth(now);
        filtered = filtered.filter(t => {
          const date = new Date(t.date);
          return date >= monthStart && date <= monthEnd;
        });
        break;
      case 'year':
        const yearStart = startOfYear(now);
        const yearEnd = endOfYear(now);
        filtered = filtered.filter(t => {
          const date = new Date(t.date);
          return date >= yearStart && date <= yearEnd;
        });
        break;
      case 'custom':
        if (customDateRange.from) {
          filtered = filtered.filter(t => {
            const date = new Date(t.date);
            return date >= customDateRange.from!;
          });
        }
        if (customDateRange.to) {
          filtered = filtered.filter(t => {
            const date = new Date(t.date);
            return date <= customDateRange.to!;
          });
        }
        break;
      case 'all':
      default:
        // Показываем все транзакции
        break;
    }

    return filtered;
  };

  // Все пользователи видят только свою статистику
  const userTransactions = getFilteredTransactions();
    
  const total = userTransactions.reduce((sum, t) => sum + Number(t.amount), 0);
  const count = userTransactions.length;
  const avg = count > 0 ? Math.round(total / count) : 0;

  const handleExportExcel = useCallback(() => {
    const csvData = [
      ['Клиент', 'Сумма', 'Направление', 'Дата'],
      ...userTransactions.map(t => [
        t.note?.split(' - ')[0] || 'Без описания',
        t.amount,
        t.note?.split(' - ')[1] || '',
        new Date(t.date).toLocaleDateString('ru-RU')
      ])
    ];
    
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Analytics_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }, [userTransactions]);

  const handleExportPowerPoint = useCallback(() => {
    const pptx = new pptxgen();
    pptx.author = currentUser?.name || 'Пользователь';
    pptx.title = 'Аналитика';
    pptx.layout = 'LAYOUT_16x9';

    // Брендовые цвета
    const brandBlue = '3B82F6';
    const brandGreen = '22C55E';
    const brandPurple = '8B5CF6';
    const darkText = '1E293B';
    const lightText = '64748B';
    const cardBg = 'F8FAFC';

    // Слайд 1: Обзор аналитики
    const slide1 = pptx.addSlide();
    
    // Градиентный заголовок
    slide1.addShape('rect', { 
      x: 0, y: 0, w: '100%', h: 1.8,
      fill: { color: brandBlue }
    });
    
    slide1.addText('Аналитика', { 
      x: 0.5, y: 0.4, fontSize: 36, bold: true, color: 'FFFFFF',
      fontFace: 'Arial'
    });
    slide1.addText(currentUser?.name || '', { 
      x: 0.5, y: 1.0, fontSize: 18, color: 'FFFFFF',
      fontFace: 'Arial'
    });
    slide1.addText(`Дата: ${new Date().toLocaleDateString('ru-RU')}`, { 
      x: 0.5, y: 1.35, fontSize: 12, color: 'E0E7FF',
      fontFace: 'Arial'
    });

    // Карточки статистики
    const cardY = 2.2;
    const cardHeight = 1.4;
    const cardWidth = 2.8;
    
    // Карточка "Общий оборот"
    slide1.addShape('roundRect', {
      x: 0.5, y: cardY, w: cardWidth, h: cardHeight,
      fill: { color: cardBg },
      line: { color: 'E2E8F0', pt: 1 },
      rectRadius: 0.1
    });
    slide1.addText('💰 Общий оборот', { 
      x: 0.7, y: cardY + 0.2, fontSize: 11, color: lightText,
      fontFace: 'Arial'
    });
    slide1.addText(`${formatNumber(total)} ₽`, { 
      x: 0.7, y: cardY + 0.6, fontSize: 22, bold: true, color: brandGreen,
      fontFace: 'Arial'
    });

    // Карточка "Количество сделок"
    slide1.addShape('roundRect', {
      x: 3.5, y: cardY, w: cardWidth, h: cardHeight,
      fill: { color: cardBg },
      line: { color: 'E2E8F0', pt: 1 },
      rectRadius: 0.1
    });
    slide1.addText('📊 Количество сделок', { 
      x: 3.7, y: cardY + 0.2, fontSize: 11, color: lightText,
      fontFace: 'Arial'
    });
    slide1.addText(`${count}`, { 
      x: 3.7, y: cardY + 0.6, fontSize: 22, bold: true, color: brandBlue,
      fontFace: 'Arial'
    });

    // Карточка "Средний чек"
    slide1.addShape('roundRect', {
      x: 6.5, y: cardY, w: cardWidth, h: cardHeight,
      fill: { color: cardBg },
      line: { color: 'E2E8F0', pt: 1 },
      rectRadius: 0.1
    });
    slide1.addText('📈 Средний чек', { 
      x: 6.7, y: cardY + 0.2, fontSize: 11, color: lightText,
      fontFace: 'Arial'
    });
    slide1.addText(`${formatNumber(avg)} ₽`, { 
      x: 6.7, y: cardY + 0.6, fontSize: 22, bold: true, color: brandPurple,
      fontFace: 'Arial'
    });

    // Слайд 2: Последние транзакции
    if (userTransactions.length > 0) {
      const slide2 = pptx.addSlide();
      
      // Заголовок
      slide2.addShape('rect', { 
        x: 0, y: 0, w: '100%', h: 1.2,
        fill: { color: brandBlue }
      });
      slide2.addText('Последние транзакции', { 
        x: 0.5, y: 0.4, fontSize: 28, bold: true, color: 'FFFFFF',
        fontFace: 'Arial'
      });

      // Таблица транзакций
      const tableData: any[] = [
        [
          { text: 'Клиент', options: { bold: true, fill: { color: brandBlue }, color: 'FFFFFF', fontFace: 'Arial' } },
          { text: 'Сумма', options: { bold: true, fill: { color: brandBlue }, color: 'FFFFFF', fontFace: 'Arial' } },
          { text: 'Направление', options: { bold: true, fill: { color: brandBlue }, color: 'FFFFFF', fontFace: 'Arial' } },
          { text: 'Дата', options: { bold: true, fill: { color: brandBlue }, color: 'FFFFFF', fontFace: 'Arial' } }
        ],
        ...userTransactions.slice(0, 10).map((t, idx) => [
          { text: t.note?.split(' - ')[0] || 'Без описания', options: { fill: { color: idx % 2 === 0 ? 'F8FAFC' : 'FFFFFF' }, fontFace: 'Arial' } },
          { text: `${formatNumber(Number(t.amount))} ₽`, options: { fill: { color: idx % 2 === 0 ? 'F8FAFC' : 'FFFFFF' }, color: brandGreen, fontFace: 'Arial' } },
          { text: t.note?.split(' - ')[1] || '-', options: { fill: { color: idx % 2 === 0 ? 'F8FAFC' : 'FFFFFF' }, fontFace: 'Arial' } },
          { text: new Date(t.date).toLocaleDateString('ru-RU'), options: { fill: { color: idx % 2 === 0 ? 'F8FAFC' : 'FFFFFF' }, fontFace: 'Arial' } }
        ])
      ];

      slide2.addTable(tableData, { 
        x: 0.5, y: 1.5, w: 9, 
        colW: [3, 2, 2.5, 1.5], 
        fontSize: 11,
        border: { type: 'solid', pt: 0.5, color: 'E2E8F0' }
      });
    }

    pptx.writeFile({ fileName: `Analytics_${new Date().toISOString().split('T')[0]}.pptx` });
    toast.success('Презентация сохранена');
  }, [userTransactions, currentUser, total, count, avg, formatNumber]);

  return (
    <div className="max-w-6xl mx-auto animate-in fade-in duration-300">
      <div className="mb-8 flex flex-col gap-4">
        <div className="flex justify-between items-start">
          <div>
            <h2 className={`text-3xl font-bold tracking-tight mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
              Добро пожаловать, {currentUser?.name}!
            </h2>
            <p className="text-slate-500">
              {dateFilter === 'month' && 'Статистика за текущий месяц'}
              {dateFilter === 'year' && 'Статистика за текущий год'}
              {dateFilter === 'all' && 'Статистика за все время'}
              {dateFilter === 'custom' && customDateRange.from && customDateRange.to && 
                `Статистика с ${format(customDateRange.from, 'dd.MM.yyyy', { locale: ru })} по ${format(customDateRange.to, 'dd.MM.yyyy', { locale: ru })}`}
              {dateFilter === 'custom' && customDateRange.from && !customDateRange.to && 
                `Статистика с ${format(customDateRange.from, 'dd.MM.yyyy', { locale: ru })}`}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold transition-colors touch-manipulation"
            >
              <Download size={16} />
              <span className="hidden md:inline">Excel</span>
            </button>
            <button
              onClick={handleExportPowerPoint}
              className="flex items-center gap-2 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-bold transition-colors touch-manipulation"
            >
              <Presentation size={16} />
              <span className="hidden md:inline">PowerPoint</span>
            </button>
          </div>
        </div>
        
        {/* Фильтры по датам */}
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setDateFilter('month')}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
              dateFilter === 'month'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
            }`}
          >
            Месяц
          </button>
          <button
            onClick={() => setDateFilter('year')}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
              dateFilter === 'year'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
            }`}
          >
            Год
          </button>
          <button
            onClick={() => setDateFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
              dateFilter === 'all'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
            }`}
          >
            Все время
          </button>
          
          {/* Календарь для выбора произвольных дат */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={dateFilter === 'custom' ? 'default' : 'outline'}
                className={`${dateFilter === 'custom' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {customDateRange.from && customDateRange.to
                  ? `${format(customDateRange.from, 'dd.MM.yy')} - ${format(customDateRange.to, 'dd.MM.yy')}`
                  : customDateRange.from
                  ? format(customDateRange.from, 'dd.MM.yyyy')
                  : 'Выбрать даты'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={{ from: customDateRange.from, to: customDateRange.to }}
                onSelect={(range) => {
                  setCustomDateRange({ from: range?.from, to: range?.to });
                  if (range?.from || range?.to) {
                    setDateFilter('custom');
                  }
                }}
                numberOfMonths={2}
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <button 
          onClick={onOpenProfit}
          className={`p-6 rounded-2xl border shadow-sm transition-all hover:shadow-md cursor-pointer ${isDark ? 'bg-slate-800 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-200 hover:border-slate-300'}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Общий оборот</div>
            <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0">
              <Wallet className="text-green-600 dark:text-green-400" size={20} />
            </div>
          </div>
          <div className={`text-3xl font-bold text-left ${isDark ? 'text-white' : 'text-slate-800'}`}>{formatNumber(total)} ₽</div>
        </button>

        <div className={`p-6 rounded-2xl border shadow-sm transition-all hover:shadow-md ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Количество сделок</div>
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
              <Calculator className="text-blue-600 dark:text-blue-400" size={20} />
            </div>
          </div>
          <div className={`text-3xl font-bold text-left ${isDark ? 'text-white' : 'text-slate-800'}`}>{count}</div>
        </div>

        <div className={`p-6 rounded-2xl border shadow-sm transition-all hover:shadow-md ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Средний чек</div>
            <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="text-purple-600 dark:text-purple-400" size={20} />
            </div>
          </div>
          <div className={`text-3xl font-bold text-left ${isDark ? 'text-white' : 'text-slate-800'}`}>{formatNumber(avg)} ₽</div>
        </div>
      </div>

      <div className={`rounded-3xl border shadow-sm overflow-hidden ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Последние транзакции
          </h3>
        </div>
        <div className="p-6">
          {userTransactions.length === 0 ? (
            <p className="text-center text-slate-400 py-8">
              У вас пока нет транзакций
            </p>
          ) : (
            <div className="space-y-4">
              {userTransactions.slice(0, 5).map((transaction: any) => (
                  <div
                    key={transaction.id}
                    className={`flex items-center justify-between p-4 rounded-xl border transition-colors group ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200'}`}
                  >
                    <div className="flex-1">
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {transaction.note?.split(' - ')[0] || 'Без описания'}
                      </p>
                      <p className="text-sm text-slate-500">
                        {transaction.note?.split(' - ')[1] && `${transaction.note.split(' - ')[1]} • `}
                        {new Date(transaction.date).toLocaleDateString('ru-RU')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-lg font-bold text-green-600">
                        +{formatNumber(Number(transaction.amount))} ₽
                      </div>
                      <button
                        onClick={() => handleDeleteTransaction(transaction.id)}
                        disabled={deletingId === transaction.id}
                        className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity p-2 disabled:opacity-50 touch-manipulation min-w-[40px] min-h-[40px] flex items-center justify-center"
                        title="Удалить транзакцию"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
